<template>
  <div>
    <b-card class="p-0 filter-card rounded-lg">
      <b-row>
        <b-col
          cols="12"
          class="d-flex justify-content-between align-items-center mb-3"
        >
          <div class="d-flex align-items-center">
            <feather-icon
              icon="FileTextIcon"
              size="14"
              class="mr-1"
            />
            <h4 class="mb-0">
              Geodesia Records
            </h4>
          </div>

          <div class="d-flex align-items-center">
            <b-button
              v-if="hasPermission('create projects')"
              variant="success"
              class="mr-1"
              @click="showImportModal = true"
            >
              <feather-icon
                icon="UploadIcon"
                size="14"
                class="mr-1"
              />
              استيراد Excel
            </b-button>

            <b-button
              v-if="hasPermission('create projects')"
              variant="primary"
              @click="openCreate"
            >
              <feather-icon
                icon="PlusIcon"
                size="14"
                class="mr-1"
              />
              إضافة سجل
            </b-button>
          </div>
        </b-col>

        
        <b-col
          cols="12"
          md="3"
        >
          <b-form-group label="كود الرفع المساحي">
            <b-form-input
              v-model.trim="filters.survey_coding"
              placeholder="مثال: SURV-123-S1"
              @input="onFilterChange"
            />
          </b-form-group>
        </b-col>

        <b-col
          cols="12"
          md="3"
        >
          <b-form-group label="بحث">
            <b-form-input
              v-model.trim="filters.search"
              placeholder="بحث بالكود جزئياً"
              @input="debounceSearch"
            />
          </b-form-group>
        </b-col>

        <b-col
          cols="12"
          md="3"
        >
          <b-form-group label="عدد الصفوف">
            <b-form-select
              v-model="filters.per_page"
              :options="perPageOptions"
              @change="onFilterChange"
            />
          </b-form-group>
        </b-col>
      </b-row>
    </b-card>

    <b-card class="mt-1">
      <b-spinner
        v-if="loading"
        class="d-block text-center my-4"
      />

      <div
        v-else-if="error"
        class="alert alert-danger"
      >
        {{ error }}
      </div>

      <b-table
        v-else
        :items="records"
        :fields="fields"
        responsive
        striped
        hover
      >
        <template #cell(actions)="row">
          <div class="d-flex align-items-center">
            <feather-icon
              v-if="hasPermission('edit projects')"
              v-b-tooltip.hover.top="'تعديل'"
              icon="EditIcon"
              size="18"
              class="text-info"
              @click="openEdit(row.item.id)"
            />
            <feather-icon
              v-if="hasPermission('delete projects')"
              v-b-tooltip.hover.top="'حذف'"
              icon="TrashIcon"
              size="18"
              class="text-danger ml-1"
              @click="confirmDelete(row.item)"
            />
          </div>
        </template>

        <template #cell(geodesia_status)="row">
          <b-badge :variant="getStatusVariant(row.item)">
            {{ getStatusLabel(row.item) }}
          </b-badge>
        </template>

        <template #cell(project_id)="row">
          {{ getProjectId(row.item) }}
        </template>
      </b-table>

      <b-pagination
        v-if="pagination && pagination.last_page > 1"
        v-model="filters.page"
        :total-rows="pagination.total"
        :per-page="pagination.per_page"
        align="center"
        class="mt-3"
        @change="onPageChange"
      />
    </b-card>

    <geodesia-record-form
      v-model="showFormModal"
      :record-id="editingRecordId"
      @saved="handleSaved"
    />

    <b-modal
      v-model="showImportModal"
      title="استيراد سجلات Geodesia من Excel/CSV"
      size="lg"
      :ok-disabled="!importFile || importing"
      ok-title="استيراد"
      cancel-title="إلغاء"
      no-close-on-backdrop
      @ok.prevent="handleImportExcel"
      @hidden="resetImportModal"
    >
      <b-form-group label="اختر ملف">
        <b-form-file
          v-model="importFile"
          accept=".xlsx,.xls,.csv"
          placeholder="اختر ملف Excel/CSV أو اسحبه هنا"
          drop-placeholder="اسحب الملف هنا"
          @change="onImportFileChange"
        />
      </b-form-group>

      <small class="text-muted d-block mb-2">
        تنسيق الأعمدة: بالترتيب `survey_code` ثم `status`.
      </small>

      <b-alert
        v-if="importError"
        variant="danger"
        show
      >
        {{ importError }}
      </b-alert>

      <div
        v-if="importing"
        class="text-center my-3"
      >
        <b-spinner class="mb-2" />
        <div class="text-muted">
          جاري الاستيراد...
        </div>
      </div>

      <div
        v-if="importResult"
        class="mt-2"
      >
        <b-alert
          variant="success"
          show
        >
          <h5 class="mb-1">
            نتائج الاستيراد
          </h5>
          <p class="mb-1">
            <strong>created:</strong>
            {{ importResult.created != null ? importResult.created : 0 }}
          </p>
          <p class="mb-1">
            <strong>updated:</strong>
            {{ importResult.updated != null ? importResult.updated : 0 }}
          </p>
          <p class="mb-1">
            <strong>skipped:</strong>
            {{ importResult.skipped != null ? importResult.skipped : 0 }}
          </p>
          <p class="mb-1">
            <strong>errors_count:</strong>
            {{ importResult.errors_count != null ? importResult.errors_count : 0 }}
          </p>
        </b-alert>

        <div
          v-if="Array.isArray(importResult.errors) && importResult.errors.length > 0"
          class="mt-2"
        >
          <b-card
            no-body
            class="border mt-2"
          >
            <b-card-body>
              <h6>الأخطاء:</h6>
              <ul class="mb-0">
                <li
                  v-for="(e, idx) in importResult.errors"
                  :key="idx"
                >
                  {{ e }}
                </li>
              </ul>
            </b-card-body>
          </b-card>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BCardBody,
  BRow,
  BCol,
  BTable,
  BButton,
  BSpinner,
  BPagination,
  BFormInput,
  BFormGroup,
  BFormFile,
  BFormSelect,
  BModal,
  BAlert,
  BBadge,
  VBTooltip,
} from 'bootstrap-vue'
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue'
import geodesiaApi from '@/api/geodesia/geodesia'
import { mapGetters } from 'vuex'
import GeodesiaRecordForm from './GeodesiaRecordForm.vue'

export default {
  name: 'GeodesiaRecordsList',
  components: {
    BCard,
    BCardBody,
    BRow,
    BCol,
    BTable,
    BButton,
    BSpinner,
    BPagination,
    BFormInput,
    BFormGroup,
    BFormFile,
    BFormSelect,
    BModal,
    BAlert,
    BBadge,
    FeatherIcon,
    'geodesia-record-form': GeodesiaRecordForm,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  computed: {
    ...mapGetters({
      userPermissions: 'users/user_permissions',
    }),
  },
  data() {
    return {
      loading: false,
      error: null,
      records: [],
      pagination: null,
      searchTimeout: null,
      filters: {
        project_id: null,
        survey_coding: '',
        search: '',
        per_page: 15,
        page: 1,
      },
      perPageOptions: [
        { value: 15, text: '15' },
        { value: 30, text: '30' },
        { value: 50, text: '50' },
        { value: 100, text: '100' },
      ],
      fields: [
        { key: 'id', label: 'ID' },
        { key: 'project_id', label: 'Project ID' },
        { key: 'survey_coding', label: 'Survey Code' },
        { key: 'geodesia_status', label: 'Geodesia Status' },
        { key: 'actions', label: '' },
      ],
      showFormModal: false,
      editingRecordId: null,

      showImportModal: false,
      importFile: null,
      importing: false,
      importError: null,
      importResult: null,
    }
  },
  mounted() {
    this.fetchRecords()
  },
  methods: {
    hasPermission(permission) {
      const perms = this.userPermissions
      if (Array.isArray(perms)) return perms.includes(permission)
      return perms && Object.values(perms).includes(permission)
    },
    getProjectId(item) {
      if (!item) return '-'
      if (item.project_id != null) return item.project_id
      if (item.project && item.project.id != null) return item.project.id
      return '-'
    },
    getStatusValue(item) {
      if (!item) return ''
      if (item.geodesia_status != null) return item.geodesia_status
      if (item.Geodesia_status != null) return item.Geodesia_status
      if (item.status != null) return item.status
      return ''
    },
    getStatusLabel(item) {
      const status = this.getStatusValue(item)
      const map = {
        pending: 'قيد التنفيذ',
        in_progress: 'جاري التنفيذ',
        completed: 'مكتمل',
      }
      return map[status] || status || '-'
    },
    getStatusVariant(item) {
      const status = this.getStatusValue(item)
      const variants = {
        pending: 'warning',
        in_progress: 'info',
        completed: 'success',
      }
      return variants[status] || 'secondary'
    },
    async fetchRecords() {
      this.loading = true
      this.error = null

      try {
        const params = { ...this.filters }
        if (!params.project_id) delete params.project_id
        if (!params.survey_coding) delete params.survey_coding
        if (!params.search) delete params.search

        const response = await geodesiaApi.listGeodesiaRecords(params)
        if (response && response.success) {
          this.records = response.data || []
          this.pagination = response.meta || null
        } else {
          this.error = (response && response.message) || 'حدث خطأ أثناء جلب البيانات'
        }
      } catch (err) {
        const message = err && err.response && err.response.data && err.response.data.message
          ? err.response.data.message
          : 'حدث خطأ أثناء جلب البيانات'
        this.error = message
      } finally {
        this.loading = false
      }
    },
    onFilterChange() {
      this.filters.page = 1
      this.fetchRecords()
    },
    debounceSearch() {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.filters.page = 1
        this.fetchRecords()
      }, 500)
    },
    onPageChange(page) {
      this.filters.page = page
      this.fetchRecords()
    },
    openCreate() {
      this.editingRecordId = null
      this.showFormModal = true
    },
    openEdit(id) {
      this.editingRecordId = id
      this.showFormModal = true
    },
    handleSaved() {
      this.showFormModal = false
      this.editingRecordId = null
      this.fetchRecords()
    },
    confirmDelete(record) {
      if (!record || !record.id) return

      this.$swal({
        title: 'هل أنت متأكد؟',
        text: 'سيتم حذف السجل (soft delete).',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'نعم، احذف!',
        cancelButtonText: 'إلغاء',
      }).then(result => {
        if (!result.isConfirmed) return

        geodesiaApi
          .deleteGeodesiaRecord(record.id)
          .then(response => {
            if (response && response.success) {
              this.$swal('تم الحذف!', 'تم حذف السجل بنجاح.', 'success')
              this.fetchRecords()
            } else {
              this.$swal('خطأ!', (response && response.message) || 'فشل الحذف', 'error')
            }
          })
          .catch(err => {
            const message = err && err.response && err.response.data && err.response.data.message
              ? err.response.data.message
              : 'حدث خطأ أثناء الحذف'
            this.$swal('خطأ!', message, 'error')
          })
      })
    },
    onImportFileChange() {
      this.importError = null
      this.importResult = null
    },
    resetImportModal() {
      this.importFile = null
      this.importing = false
      this.importError = null
      this.importResult = null
    },
    async handleImportExcel() {
      if (!this.importFile) {
        this.$toast.error('يرجى اختيار ملف')
        return
      }

      this.importing = true
      this.importError = null
      this.importResult = null

      try {
        const response = await geodesiaApi.importGeodesiaRecordsFromExcel(this.importFile)
        if (response && response.success) {
          this.importResult = response.data || {}
          this.$toast.success('تم الاستيراد بنجاح')
          this.showImportModal = false
          await this.fetchRecords()
        } else {
          const message = (response && response.message) || 'فشل الاستيراد'
          this.importError = message
          this.$toast.error(message)
        }
      } catch (err) {
        const message = err && err.response && err.response.data && err.response.data.message
          ? err.response.data.message
          : 'فشل الاستيراد'
        this.importError = message
        this.$toast.error(message)
      } finally {
        this.importing = false
      }
    },
  },
}
</script>

<style scoped>
.filter-card {
  margin-bottom: 1rem;
}
</style>
